<template>
    <div class="optiongroup-tile" v-show="optionGroup.options.length > 0">
        <div class="name">{{ groupName }}</div>
        <div class="instruction">{{ instructionText }}</div>

        <div class="options">
            <div
                v-for="state in optionStates"
                :key="state.option.articleId"
                class="option"
                :class="{ selected: state.selected, disabled: isOptionDisabled(state) }"
                @click="onClickOption(state)"
            >
                <!-- this box is 155px * $kioskScale like the item tiles, so it takes the 700x700
                     kiosk variant - the 28px .parent-image below stays on the 148x148 thumbnail -->
                <div class="image" v-if="state.option.imagePath">
                    <img
                        loading="lazy"
                        decoding="async"
                        :src="state.option.kioskImagePath ?? state.option.imagePath"
                        alt=""
                    />
                </div>

                <div class="selector">
                    <div v-if="isRadio" class="radiobox"></div>
                    <div v-else class="checkbox"><i class="fas fa-check"></i></div>
                </div>

                <div class="option-name">
                    <span v-if="hasQuantity && state.quantity > 0" class="qty">{{ state.quantity }}x</span>
                    {{ optionName(state.option) }}<ItemIcons :articleId="state.option.articleId" includeOrderedBefore :mainArticleId="props.mainArticleId" />
                </div>

                <!-- options carry flags on the kiosk only, and this tile is the kiosk one -->
                <ItemFlags :articleId="state.option.articleId" :mainArticleId="props.mainArticleId" />

                <div class="price" v-if="priceLabel(state.option)" @click.stop>{{ priceLabel(state.option) }}</div>

                <div class="quantity" v-if="hasQuantity && showQuantityStepper(state)" @click.stop>
                    <div class="minus btn" :class="{ disabled: !canDecrease(state) }" @click="decreaseQuantity(state)">
                        <i class="fa fa-minus"></i>
                    </div>
                    <div class="plus btn" :class="{ disabled: !canIncrease(state) }" @click="increaseQuantity(state)">
                        <i class="fa fa-plus"></i>
                    </div>
                </div>
            </div>
        </div>

        <!-- sub-option-groups render full width, outside the options grid -->
        <template v-for="(state, optIndex) in optionStates" :key="state.option.articleId + '-sub'">
            <div class="subgroups" v-if="state.selected && state.option.optionGroups.length > 0">
                <div class="subgroups-parent">
                    <img
                        decoding="async"
                        loading="lazy"
                        v-if="state.option.imagePath"
                        class="parent-image"
                        :src="state.option.imagePath"
                        alt=""
                    />
                    <span class="parent-name">{{ optionName(state.option) }}</span>
                </div>
                <OptionGroupTile
                    v-for="(subGroup, subIndex) in state.option.optionGroups"
                    :key="subGroup.optionGroupId"
                    :ref="(el) => setSubGroupRef(`${optIndex}:${subIndex}`, el)"
                    :option-group="subGroup"
                    :main-article-id="props.mainArticleId"
                    :show-plus-on-prices="subGroupShowPlusOnPrices(state.option)"
                    @change="onSubGroupChange"
                />
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
    import { trans } from "@/app/localization/Trans";
    import { Helper } from "@/app/ui/Helper";
    import type { IArticleWithOptions } from "@/data/commands/customer/AddArticleToCartCmd";
    import type { ICustomerOptionGroup, ICustomerOptionGroupOption } from "@/data/model/Customer";
    import { Format } from "@/data/util/Format";
    import { clientLanguage } from "@/shared/localization/language";
    import { computed, onMounted, ref, watch } from "vue";

    import ItemFlags from "@/components/order/ItemFlags.vue";
    import ItemIcons from "@/components/order/ItemIcons.vue";

    interface IOptionState {
        option: ICustomerOptionGroupOption;
        selected: boolean;
        quantity: number;
    }

    const props = defineProps<{
        optionGroup: ICustomerOptionGroup;
        // the top-level main item the options are offered in, also for the groups of an option
        mainArticleId: string;
        showPlusOnPrices: boolean;
    }>();

    const emit = defineEmits<{ change: [] }>();

    const optionStates = ref<IOptionState[]>([]);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const subGroupRefs = ref<Record<string, any>>({});

    const isRadio = computed(() => props.optionGroup.minOptions === 1 && props.optionGroup.maxOptions === 1);
    const hasQuantity = computed(() => props.optionGroup.maxPerOption !== 1);

    // clamp max options to the number of options (mirrors OptionGroupRow)
    const maxOptions = computed(() => {
        const max = props.optionGroup.maxOptions;
        if (max === undefined) return undefined;
        return Math.min(max, props.optionGroup.options.length);
    });

    const selectedStates = computed(() => optionStates.value.filter((s) => s.selected));
    const totalQuantity = computed(() =>
        hasQuantity.value ? selectedStates.value.reduce((sum, s) => sum + s.quantity, 0) : selectedStates.value.length
    );

    const groupName = computed(() => Format.translate(props.optionGroup.name, clientLanguage()) ?? "");

    const instructionText = computed(() => buildInstructionText());

    function initStates() {
        subGroupRefs.value = {};
        optionStates.value = props.optionGroup.options.map((option) => {
            const selected = option.preselected;
            const quantity = selected && hasQuantity.value ? (option.minQuantity ?? 1) : 0;
            return { option, selected, quantity };
        });
    }

    onMounted(initStates);
    watch(() => props.optionGroup, initStates);

    function optionName(option: ICustomerOptionGroupOption) {
        return Format.translate(option.name, clientLanguage()) ?? "";
    }

    function priceLabel(option: ICustomerOptionGroupOption): string {
        const price = Helper.formatPrice(option);
        if (price === "") return "";

        return (props.showPlusOnPrices && Helper.sitePrice(option) > 0 ? "+" : "") + price;
    }

    function showQuantityStepper(state: IOptionState): boolean {
        return (state.option.minQuantity ?? 1) !== state.option.maxQuantity;
    }

    function subGroupShowPlusOnPrices(option: ICustomerOptionGroupOption): boolean {
        return option.price !== undefined && option.price !== 0;
    }

    function setSubGroupRef(key: string, el: unknown) {
        if (el) subGroupRefs.value[key] = el;
        else delete subGroupRefs.value[key];
    }

    // ---- selection state ----

    function allowNewOption(): boolean {
        if (isRadio.value) return true;
        if (props.optionGroup.maxSum !== undefined && props.optionGroup.maxSum <= totalQuantity.value) return false;
        if (maxOptions.value !== undefined && maxOptions.value <= selectedStates.value.length) return false;
        return true;
    }

    function isOptionDisabled(state: IOptionState): boolean {
        if (isRadio.value || state.selected) return false;
        return !allowNewOption();
    }

    function canIncrease(state: IOptionState): boolean {
        if (!hasQuantity.value) return false;

        const og = props.optionGroup;
        if (og.maxSum !== undefined && og.maxSum <= totalQuantity.value) return false;
        if (og.maxPerOption !== undefined && og.maxPerOption <= state.quantity) return false;
        if (
            og.maxPerOption === undefined &&
            state.option.maxQuantity !== undefined &&
            state.quantity >= state.option.maxQuantity
        )
            return false;
        if (!state.selected && !allowNewOption()) return false;

        return true;
    }

    function canDecrease(state: IOptionState): boolean {
        if (!state.selected) return false;
        return state.quantity > (state.option.minQuantity ?? 1);
    }

    function selectOption(state: IOptionState, updateQuantity = true) {
        if (isRadio.value) {
            optionStates.value.forEach((s) => {
                if (s !== state) deselectOption(s, true);
            });
        }

        if (!state.selected) {
            state.selected = true;
            if (updateQuantity && hasQuantity.value) state.quantity = state.option.minQuantity ?? 1;
        }
    }

    function deselectOption(state: IOptionState, updateQuantity = true) {
        if (state.selected) {
            state.selected = false;
            if (updateQuantity && hasQuantity.value) state.quantity = 0;
        }
    }

    function onClickOption(state: IOptionState) {
        if (isOptionDisabled(state)) return;

        if (isRadio.value) {
            if (state.selected) return;
            selectOption(state);
        } else {
            if (state.selected) deselectOption(state);
            else selectOption(state);
        }

        emit("change");
    }

    function increaseQuantity(state: IOptionState) {
        if (!hasQuantity.value || !canIncrease(state)) return;

        let newQuantity = state.quantity + 1;
        if (state.option.minQuantity !== undefined && newQuantity < state.option.minQuantity)
            newQuantity = state.option.minQuantity;
        if (props.optionGroup.maxPerOption !== undefined && newQuantity > props.optionGroup.maxPerOption) return;

        const wasZero = state.quantity === 0;
        state.quantity = newQuantity;
        if (wasZero) selectOption(state, false);

        emit("change");
    }

    function decreaseQuantity(state: IOptionState) {
        if (!hasQuantity.value || !canDecrease(state)) return;

        const newQuantity = state.quantity - 1;
        if (newQuantity < 0) return;

        state.quantity = newQuantity;
        if (newQuantity === 0) deselectOption(state, false);

        emit("change");
    }

    function onSubGroupChange() {
        emit("change");
    }

    // ---- exposed API (mirrors OptionGroupRow) ----

    function getValue(): IArticleWithOptions[] {
        const result: IArticleWithOptions[] = [];

        optionStates.value.forEach((state, optIndex) => {
            if (!state.selected) return;

            const entry: IArticleWithOptions = {
                quantity: hasQuantity.value ? state.quantity : 1,
                menuCategoryId: undefined,
                articleId: state.option.articleId,
                optionGroupId: props.optionGroup.optionGroupId,
                options: [],
            };

            state.option.optionGroups.forEach((_subGroup, subIndex) => {
                const child = subGroupRefs.value[`${optIndex}:${subIndex}`];
                if (child) entry.options.push(...child.getValue());
            });

            result.push(entry);
        });

        return result;
    }

    function isValid(): boolean {
        const og = props.optionGroup;
        const selectedCount = selectedStates.value.length;
        const childRefs = Object.values(subGroupRefs.value);

        if (og.minOptions === undefined && maxOptions.value === undefined && childRefs.length === 0) return true;

        if (og.minOptions !== undefined && og.minOptions > selectedCount) return false;
        if (maxOptions.value !== undefined && maxOptions.value < selectedCount) return false;

        for (const child of childRefs) {
            if (!child.isValid()) return false;
        }

        return true;
    }

    function calculatePrice(): number {
        let price = 0;

        optionStates.value.forEach((state, optIndex) => {
            if (!state.selected) return;

            const quantity = hasQuantity.value ? state.quantity : 1;
            const optionPrice = Helper.sitePrice(state.option);
            if (optionPrice !== undefined && optionPrice !== 0) price += quantity * optionPrice;

            state.option.optionGroups.forEach((_subGroup, subIndex) => {
                const child = subGroupRefs.value[`${optIndex}:${subIndex}`];
                if (child) price += child.calculatePrice() * quantity;
            });
        });

        return price;
    }

    function buildInstructionText(): string {
        const og = props.optionGroup;
        const p = trans.addToCartPopover;
        const min = og.minOptions === undefined || og.minOptions === 0 ? undefined : og.minOptions;
        const max = maxOptions.value;

        let text = "";
        if (min === undefined && (max === undefined || max === 1 || max === og.options.length)) {
            text += p.optional + ".";
        } else if (min !== undefined && og.minOptions === max) {
            text += p.chooseExact.replace("{0}", min + "").replace("{1}", min > 1 ? p.optionPlur : p.optionSing) + ".";
        } else if (max === undefined && min !== undefined) {
            text += p.chooseMin.replace("{0}", min + "").replace("{1}", min > 1 ? p.optionPlur : p.optionSing) + ".";
        } else if (max !== undefined && min === undefined) {
            text += p.chooseUpTo.replace("{0}", max + "").replace("{1}", max > 1 ? p.optionPlur : p.optionSing) + ".";
        } else if (max !== undefined && min !== undefined) {
            text += p.chooseBetween.replace("{0}", min + "").replace("{1}", max + "") + ".";
        }

        if (
            og.maxSum !== undefined &&
            og.maxSum > 1 &&
            og.options.length > 1 &&
            (max === undefined || max > 1) &&
            (og.maxPerOption === undefined || og.maxPerOption > 1)
        ) {
            text += " " + p.upToTotalItems.replace("{0}", og.maxSum + "") + ".";
        }

        if (text.length !== 0) text = text.substring(0, text.length - 1);

        return text;
    }

    defineExpose({
        getValue,
        isValid,
        calculatePrice,
    });
</script>

<style scoped lang="scss">
    @use "./../../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../../assets/styles/scss/shared/_variables.scss";

    // kiosk: larger group name and instruction
    .kiosk-mode .optiongroup-tile {
        > .name {
            font-size: 18px * variables.$kioskScale;
        }

        > .instruction {
            font-size: 16px * variables.$kioskScale;
            color: colors.$fontColor;
        }
    }

    .optiongroup-tile {
        margin-bottom: 18px;

        > .name {
            font-size: 18px;
            font-weight: 600;
            color: colors.$fontColor;
        }

        > .instruction {
            font-size: 14px * variables.$kioskScale;
            color: colors.$lightGrey;
            margin-bottom: 10px;
        }

        > .options {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(240px * variables.$kioskScale, 1fr));
            gap: 10px * variables.$kioskScale;
        }

        // sub-groups render full width, in their own row below the options grid
        > .subgroups {
            margin-top: 14px;
            padding-left: 14px;
            border-left: 3px solid var(--colorPrimary);

            > .subgroups-parent {
                display: flex;
                align-items: center;
                gap: 8px;
                margin-bottom: 10px;

                > .parent-image {
                    width: 28px;
                    height: 28px;
                    object-fit: contain;
                }

                > .parent-name {
                    font-size: 16px * variables.$kioskScale;
                    font-weight: 700;
                    color: colors.$fontColor;
                }
            }
        }
    }

    .option {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: (15px * variables.$kioskScale);
        background: #fff;
        border: 1px solid #dcdcdc;
        border-radius: 8px;
        cursor: pointer;
        transition: border-color 0.15s linear;

        > .image {
            // the same box as the item tiles on the offer overview (OfferItemTile): their
            // 150px kiosk image, and the 2em the tile leaves above and below it - taken off
            // the tile's own kiosk font-size (15px in KioskOfferOverview) so the gap lands
            // on the same px
            width: 155px * variables.$kioskScale;
            height: 155px * variables.$kioskScale;
            margin-bottom: 1.25em;

            > img {
                width: 100%;
                height: 100%;
                object-fit: cover;
                border-radius: 0.25em;
            }
        }

        > .selector {
            position: absolute;
            top: 8px;
            left: 8px;

            > .radiobox,
            > .checkbox {
                width: 18px * variables.$kioskScale;
                height: 18px * variables.$kioskScale;
                border: 2px solid #ccc;
                background: #fff;
            }

            > .radiobox {
                border-radius: 50%;
            }

            > .checkbox {
                border-radius: 4px;
                display: flex;
                align-items: center;
                justify-content: center;

                > i {
                    display: none;
                    font-size: 11px;
                    line-height: 1;
                    color: #fff;
                }
            }
        }

        > .option-name {
            // pinned to the bottom of the tile: the grid stretches every option to the
            // height of the tallest one in its row, and the free space goes above the name
            // instead of below it. an option without an image gets the same treatment,
            // so its name lines up with the ones that have one
            margin-top: auto;
            text-align: center;
            margin-left: 24px;
            margin-right: 24px;
            font-size: 18px * variables.$kioskScale;
            color: colors.$fontColor;
            font-weight: 600;
            word-break: break-word;

            > .qty {
                color: var(--colorPrimary);
                font-weight: 600;
            }
        }

        // the flags follow the name, on the side margins the name keeps, so a wrapped row
        // lines up with the text above it
        > .item-flags {
            justify-content: center;
            max-width: 100%;
            margin: (6px * variables.$kioskScale) 24px 0;
        }

        > .price {
            margin-top: 4px;
            font-size: 16px * variables.$kioskScale;
            color: colors.$fontColor;
            font-weight: 400;
        }

        > .quantity {
            display: flex;
            gap: 18px * variables.$kioskScale;
            margin-top: 12px * variables.$kioskScale;

            > .btn {
                width: 32px * variables.$kioskScale;
                height: 32px * variables.$kioskScale;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 50%;
                background: var(--colorPrimary);
                color: #fff;
                // the glyph has no size of its own, it inherits the 16px document base
                font-size: 12px * variables.$kioskScale;

                &.disabled {
                    opacity: 0.35;
                    pointer-events: none;
                }
            }
        }

        &.selected {
            border-color: var(--colorPrimary);

            > .selector {
                > .radiobox {
                    border-color: var(--colorPrimary);
                    background: radial-gradient(var(--colorPrimary) 0 45%, #fff 55% 100%);
                }

                > .checkbox {
                    border-color: var(--colorPrimary);
                    background: var(--colorPrimary);

                    > i {
                        display: block;
                    }
                }
            }
        }

        &.disabled {
            opacity: 0.4;
            cursor: default;
        }
    }
</style>
